/**
 * AgentBus — debounced, confidence-gated pub/sub for the agentic canvas
 * substrate. Wires the pure core (types.ts) into a live in-browser channel.
 *
 * Subscribers (Rules Builder, future 2D/3D canvases) receive Suggestions
 * after the substrate has already applied evaluateProposal + dedup +
 * expiry. Producers (validators, agent loops) call emit() with
 * AgentProposals; the bus decides whether a Suggestion materialises.
 *
 * UX contract (docs/specs/08-agentic-canvas.md §UX):
 *   · ghost-never-block        (ghosts are added, never modal)
 *   · designer-pull             (subscribers ignore events freely)
 *   · confidence-graded silence (evaluateProposal gates it)
 *   · single-key fluency        (bus dispatch is keyed by Suggestion.id)
 *   · quiet sidebar             (subscribers paint, never count toasts)
 *   · sticky off-switch         (closeOpen on toggle)
 *
 * No setInterval, no setTimeout in tests — expiry is driven by `tick(now)`
 * so unit tests stay deterministic. The real-time tick is owned by the
 * subscriber (e.g. requestAnimationFrame in BuilderTab).
 */
import {
  AGENT_CONFIG_DEFAULTS,
  AgentConfig,
  AgentProposal,
  Suggestion,
  closeOpen,
  deriveDedupKey,
  evaluateProposal,
  isExpired,
  transition,
} from "./types";

export interface BusEvent {
  /** Why this Suggestion was emitted/updated — for subscriber rendering. */
  readonly kind: "emit" | "expire" | "accept" | "reject";
  readonly suggestion: Suggestion;
}

export type BusListener = (e: BusEvent) => void;

export interface AgentBus {
  /** Propose a Suggestion. Returns the resulting state — either an emitted
   *  Suggestion or a typed suppression reason. Idempotent on dedup key. */
  emit(proposal: AgentProposal, now?: Date): {
    readonly emitted: boolean;
    readonly suggestion?: Suggestion;
    readonly reason?: "agent-off" | "below-mute-threshold" | "deduped";
  };
  /** Subscribe to bus events. Returns an unsubscribe. */
  subscribe(listener: BusListener): () => void;
  /** Apply a transition to a known Suggestion. */
  apply(id: string, action: "accept" | "reject", now?: Date): Suggestion | null;
  /** Tick — expire any ghosts whose expiresAt has passed. Subscriber drives. */
  tick(now: Date): ReadonlyArray<Suggestion>;
  /** Toggle the agent. Off-state flips every open ghost to expired (UX 6). */
  setAgentOn(on: boolean): void;
  /** Read-only snapshot of currently-tracked Suggestions. */
  snapshot(): ReadonlyArray<Suggestion>;
}

interface AgentBusOptions {
  readonly config?: AgentConfig;
  readonly clock?: () => Date;
}

export function createAgentBus(opts: AgentBusOptions = {}): AgentBus {
  let config: AgentConfig = opts.config ?? AGENT_CONFIG_DEFAULTS;
  const clock = opts.clock ?? (() => new Date());
  /** id → Suggestion (open and terminal — UX rule 8 keeps history). */
  const byId = new Map<string, Suggestion>();
  /** dedup key → open Suggestion id (only ghosts get deduped). */
  const dedupIndex = new Map<string, string>();
  const listeners = new Set<BusListener>();

  function notify(e: BusEvent): void {
    // Iterate a stable snapshot so a listener unsubscribing during dispatch
    // doesn't skip others. CLAUDE.md §5 — never silently swallow.
    const snap = Array.from(listeners);
    for (const l of snap) {
      try {
        l(e);
      } catch (err) {
        // Re-emit as a console-grade error so the gate catches drift in dev
        // but the bus doesn't take other subscribers down with it.
        // eslint-disable-next-line no-console
        console.error("[AgentBus] listener threw", err);
      }
    }
  }

  return {
    emit(proposal, now): { emitted: boolean; suggestion?: Suggestion; reason?: "agent-off" | "below-mute-threshold" | "deduped" } {
      const stamp = now ?? clock();
      const dedup = deriveDedupKey(proposal);
      const existingId = dedupIndex.get(dedup);
      if (existingId) {
        const existing = byId.get(existingId);
        if (existing && existing.status === "ghost") {
          return { emitted: false, suggestion: existing, reason: "deduped" };
        }
      }
      const result = evaluateProposal(proposal, config, stamp);
      if (!result.emitted) {
        return { emitted: false, reason: result.reason };
      }
      byId.set(result.suggestion.id, result.suggestion);
      dedupIndex.set(dedup, result.suggestion.id);
      notify({ kind: "emit", suggestion: result.suggestion });
      return { emitted: true, suggestion: result.suggestion };
    },

    subscribe(listener): () => void {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    apply(id, action, now): Suggestion | null {
      const current = byId.get(id);
      if (!current) return null;
      if (current.status !== "ghost") return current;
      const stamp = now ?? clock();
      void stamp; // reserved for future activity-log timestamps
      const next = transition(current, action);
      byId.set(id, next);
      // Free the dedup slot so a future proposal with the same key can emit
      // a fresh Suggestion (the current one is now history).
      const dedupKey = findDedupKeyById(dedupIndex, id);
      if (dedupKey !== undefined) dedupIndex.delete(dedupKey);
      notify({ kind: action, suggestion: next });
      return next;
    },

    tick(now): ReadonlyArray<Suggestion> {
      const expired: Suggestion[] = [];
      for (const [id, sug] of byId) {
        if (sug.status !== "ghost") continue;
        if (!isExpired(sug, now)) continue;
        const next: Suggestion = { ...sug, status: "expired" };
        byId.set(id, next);
        const dedupKey = findDedupKeyById(dedupIndex, id);
        if (dedupKey !== undefined) dedupIndex.delete(dedupKey);
        expired.push(next);
        notify({ kind: "expire", suggestion: next });
      }
      return expired;
    },

    setAgentOn(on): void {
      const next: AgentConfig = { ...config, agentOn: on };
      config = next;
      if (!on) {
        const open = Array.from(byId.values());
        const closed = closeOpen(open, "agent-off");
        for (const s of closed) {
          byId.set(s.id, s);
          if (s.status === "expired") {
            const dedupKey = findDedupKeyById(dedupIndex, s.id);
            if (dedupKey !== undefined) dedupIndex.delete(dedupKey);
            notify({ kind: "expire", suggestion: s });
          }
        }
      }
    },

    snapshot(): ReadonlyArray<Suggestion> {
      return Array.from(byId.values());
    },
  };
}

function findDedupKeyById(index: Map<string, string>, id: string): string | undefined {
  for (const [k, v] of index) if (v === id) return k;
  return undefined;
}
